import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Bell } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";

interface NotificationSettingsProps {
  loading?: boolean;
  onSave?: (settings: NotificationPreferences) => Promise<void>;
}

export interface NotificationPreferences {
  serviceRequest: boolean;
  serviceAccepted: boolean;
  serviceReminder: boolean;
  dailySummary: boolean;
  serviceReschedule: boolean;
  customerReminder: boolean;
}

const NotificationSettings = ({
  loading: externalLoading,
  onSave
}: NotificationSettingsProps) => {
  const { toast } = useToast();
  const { t } = useTranslation();
  const [serviceRequest, setServiceRequest] = useState(true);
  const [serviceAccepted, setServiceAccepted] = useState(true);
  const [serviceReminder, setServiceReminder] = useState(true);
  const [dailySummary, setDailySummary] = useState(true);
  const [serviceReschedule, setServiceReschedule] = useState(true);
  const [customerReminder, setCustomerReminder] = useState(true);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  
  useEffect(() => {
    const fetchNotificationPreferences = async () => {
      try {
        setInitialLoading(true);
        
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          console.error("No authenticated user found");
          setInitialLoading(false);
          return;
        }

        const { data, error } = await supabase
          .from('notification_preferences')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();
        
        if (error) {
          console.error("Error fetching notification preferences:", error);
          toast({
            title: "Error",
            description: "Failed to load notification preferences",
            variant: "destructive"
          });
          setInitialLoading(false);
          return;
        }
        
        if (data) {
          setServiceRequest(data.service_request);
          setServiceAccepted(data.service_accepted);
          setServiceReminder(data.service_reminder);
          setDailySummary(data.daily_summary);
          setServiceReschedule(data.service_reschedule);
          setCustomerReminder(data.customer_reminder);
        } else {
          const { error: insertError } = await supabase
            .from('notification_preferences')
            .insert({
              user_id: user.id,
              service_request: true,
              service_accepted: true,
              service_reminder: true,
              daily_summary: true,
              service_reschedule: true,
              customer_reminder: true
            });
            
          if (insertError) {
            console.error("Error creating default notification preferences:", insertError);
            toast({
              title: "Error",
              description: "Failed to create default notification preferences",
              variant: "destructive"
            });
          }
        }
      } catch (error) {
        console.error("Unexpected error:", error);
        toast({
          title: "Error",
          description: "An unexpected error occurred",
          variant: "destructive"
        });
      } finally {
        setInitialLoading(false);
      }
    };

    fetchNotificationPreferences();
  }, [toast]);

  const handleSave = async () => {
    try {
      setLoading(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.error("No authenticated user found");
        toast({
          title: "Error",
          description: "User authentication required",
          variant: "destructive"
        });
        return;
      }

      const preferences = {
        service_request: serviceRequest,
        service_accepted: serviceAccepted,
        service_reminder: serviceReminder,
        daily_summary: dailySummary,
        service_reschedule: serviceReschedule,
        customer_reminder: customerReminder
      };

      const { error } = await supabase
        .from('notification_preferences')
        .update(preferences)
        .eq('user_id', user.id);
        
      if (error) {
        console.error("Error updating notification preferences:", error);
        toast({
          title: "Error",
          description: "Failed to save notification preferences",
          variant: "destructive"
        });
        return;
      }

      if (onSave) {
        await onSave({
          serviceRequest,
          serviceAccepted,
          serviceReminder,
          dailySummary,
          serviceReschedule,
          customerReminder
        });
      } else {
        toast({
          title: "Success",
          description: "Notification preferences saved successfully",
        });
      }
    } catch (error) {
      console.error("Unexpected error:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const isLoading = loading || externalLoading || initialLoading;

  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        {initialLoading ? (
          <div className="flex justify-center py-6">
            <img 
              src="/lovable-uploads/f8714beb-a432-4da6-acab-3ef6f0a42367.png" 
              alt="Loading notification settings" 
              className="h-8 w-8 gebeya-spinner" 
            />
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <div className="flex items-start gap-3">
                <Bell className="h-5 w-5 text-gray-500 mt-0.5" />
                <div>
                  <h4 className="font-medium">{t('settings.serviceRequestNotifications')}</h4>
                  <p className="text-sm text-gray-500">{t('settings.serviceRequestNotificationsDesc')}</p>
                </div>
              </div>
              <Switch 
                checked={serviceRequest} 
                onCheckedChange={setServiceRequest}
                className="data-[state=checked]:bg-gebeya-pink"
              />
            </div>
            
            <div className="flex items-center justify-between border-t pt-4">
              <div className="flex items-start gap-3">
                <Bell className="h-5 w-5 text-gray-500 mt-0.5" />
                <div>
                  <h4 className="font-medium">{t('settings.serviceAcceptedNotifications')}</h4>
                  <p className="text-sm text-gray-500">{t('settings.serviceAcceptedNotificationsDesc')}</p>
                </div>
              </div>
              <Switch 
                checked={serviceAccepted} 
                onCheckedChange={setServiceAccepted}
                className="data-[state=checked]:bg-gebeya-pink"
              />
            </div>
            
            <div className="flex items-center justify-between border-t pt-4">
              <div className="flex items-start gap-3">
                <Bell className="h-5 w-5 text-gray-500 mt-0.5" />
                <div>
                  <h4 className="font-medium">{t('settings.serviceReminderNotifications')}</h4>
                  <p className="text-sm text-gray-500">{t('settings.serviceReminderNotificationsDesc')}</p>
                </div>
              </div>
              <Switch 
                checked={serviceReminder} 
                onCheckedChange={setServiceReminder}
                className="data-[state=checked]:bg-gebeya-pink"
              />
            </div>

            <div className="flex items-center justify-between border-t pt-4">
              <div className="flex items-start gap-3">
                <Bell className="h-5 w-5 text-gray-500 mt-0.5" />
                <div>
                  <h4 className="font-medium">{t('settings.dailySummary')}</h4>
                  <p className="text-sm text-gray-500">{t('settings.dailySummaryDesc')}</p>
                </div>
              </div>
              <Switch 
                checked={dailySummary} 
                onCheckedChange={setDailySummary}
                className="data-[state=checked]:bg-gebeya-pink"
              />
            </div>

            <div className="flex items-center justify-between border-t pt-4">
              <div className="flex items-start gap-3">
                <Bell className="h-5 w-5 text-gray-500 mt-0.5" />
                <div>
                  <h4 className="font-medium">{t('settings.serviceRescheduleNotifications')}</h4>
                  <p className="text-sm text-gray-500">{t('settings.serviceRescheduleNotificationsDesc')}</p>
                </div>
              </div>
              <Switch 
                checked={serviceReschedule} 
                onCheckedChange={setServiceReschedule}
                className="data-[state=checked]:bg-gebeya-pink"
              />
            </div>

            <div className="flex items-center justify-between border-t pt-4">
              <div className="flex items-start gap-3">
                <Bell className="h-5 w-5 text-gray-500 mt-0.5" />
                <div>
                  <h4 className="font-medium">{t('settings.customerReminderNotifications')}</h4>
                  <p className="text-sm text-gray-500">{t('settings.customerReminderNotificationsDesc')}</p>
                </div>
              </div>
              <Switch 
                checked={customerReminder} 
                onCheckedChange={setCustomerReminder}
                className="data-[state=checked]:bg-gebeya-pink"
              />
            </div>
            
            <Button 
              className="mt-4 bg-gradient-to-r from-gebeya-pink to-gebeya-orange hover:opacity-90 transition-opacity text-white"
              onClick={handleSave}
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <img 
                    src="/lovable-uploads/f8714beb-a432-4da6-acab-3ef6f0a42367.png" 
                    alt="Saving" 
                    className="h-4 w-4 gebeya-spinner" 
                  />
                  <span>{t('settings.saving')}</span>
                </div>
              ) : t('settings.saveChanges')}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default NotificationSettings;
